import { v4 } from 'uuid'
import { IApiConfig, IDomConfig } from '../@types/config'
import { Mime } from '../constants/ima'
import {
  ACTIVE_ID,
  AUTOPLAY_DISABLED,
  NAMESPACE,
  PLAYLIST_POSITION
} from '../constants/storage'
import { PopoutPlacement, PREFIX_PLAYLIST } from '../constants/styles'
import { CLOUDINARY_SHIELD_URL, CLOUDINARY_URL } from '../constants/url'
import {
  AdUnit,
  DEFAULT_VOLUME,
  SourceFormat,
  SourceType,
  VideoStatus
} from '../constants/video'
import { detectMimeTypes } from './mime-types'
import { Device } from './device'
import { Storage } from './storage'

const supportH265 = Device.supportsVideoFormat(SourceFormat.h265)
const supportVP9 = Device.supportsVideoFormat(SourceFormat.vp9)

export class Config {
  public mimeTypes: Mime[]
  public mobile: boolean
  public npa: 0 | 1 = 0
  public playlist: IPlaylistItem[]
  public popoutPlacement: PopoutPlacement
  public popoutTopMargin: string
  public storage: Storage
  public startingPosition: number
  public targetDiv: HTMLDivElement
  public uuid: string

  constructor(
    public readonly apiConfig: IApiConfig,
    public readonly domConfig: Partial<IDomConfig>
  ) {
    this.mimeTypes = detectMimeTypes()
    this.mobile = Device.mobile
    this.storage = new Storage(NAMESPACE, this.slug)
    this.startingPosition = this.storage.get(PLAYLIST_POSITION) || 0
    // ^ needs to be first because used elsewhere

    this.targetDiv = this.getTargetDiv()
    this.uuid = v4()
    // ^ needs to be second because other property calculations are based on it

    this.playlist = this.createPlaylist()
    this.popoutPlacement = this.getPopoutPlacement()
    this.popoutTopMargin = this.getPopoutTopMargin()
  }

  get active(): boolean {
    return this.storage.get(ACTIVE_ID, true) === this.uuid
  }

  get adunitId(): string {
    return `${this.slug}__${this.uuid}`
  }

  get aspectFraction(): number {
    const [width, height] = this.aspectRatio.split(':')
    return parseFloat(width) / parseFloat(height)
  }

  get aspectRatio(): string {
    const { domConfig, apiConfig } = this
    let ratio

    if (domConfig.aspectRatio === 'default') {
      ratio = apiConfig.site.aspectRatio || '16:9'
    } else {
      ratio = domConfig.aspectRatio || apiConfig.site.aspectRatio || '16:9'
    }
    return ratio === 'fluid' ? `${this.width}:${this.height}` : ratio
  }

  get autoplay(): boolean {
    if (
      this.apiConfig.video.disableAutoplayOnClose &&
      this.storage.get(AUTOPLAY_DISABLED)
    ) {
      return false
    }

    // mobile
    if (this.domConfig.isAutomobile && this.mobile) {
      return true
    }

    // desktop
    if (this.domConfig.isAutoplay && !this.mobile) {
      return true
    }

    return false
  }

  get customTargeting(): object | undefined {
    return this.domConfig.customTargeting || {}
  }

  get contentHeight(): number {
    return Math.round(this.contentWidth / this.aspectFraction)
  }

  get contentWidth(): number {
    const wrapper = this.targetDiv
    if (!wrapper) {
      return 0
    }
    return wrapper.offsetWidth
  }

  get debug(): boolean {
    const params = new URLSearchParams(window.location.search)
    return params.has('debug')
  }

  get disableJsonLD(): boolean {
    return !!this.domConfig.disableJsonLD
  }

  get height(): number {
    return this.domConfig.height || this.apiConfig.video.height || 0
  }

  get muted(): boolean {
    return !!(this.domConfig.isMuted || this.autoplay)
  }

  get onAdStarted(): () => void {
    return this.domConfig.onAdStarted || (() => null)
  }

  get onEnded(): () => void {
    return () => {
      if (this.domConfig.onEnded) {
        this.domConfig.onEnded()
      }
    }
  }

  get onError(): (...props) => void {
    return (...props) => {
      if (this.domConfig.onError) {
        this.domConfig.onError(...props)
      }
    }
  }

  get onViewable(): () => void {
    return this.domConfig.onViewable || (() => null)
  }

  get permalink(): string {
    return (
      this.domConfig.permalink ||
      this.apiConfig.video.permalink ||
      `${window.location.protocol}//${this.apiConfig.site.domain}`
    )
  }

  get processing(): boolean {
    return this.apiConfig.video.status === VideoStatus.processing
  }

  get slug(): string {
    return this.apiConfig.video.slug || ''
  }

  get sticky(): boolean {
    const sticky = !!this.domConfig.sticky
    return !!(
      (sticky && this.domConfig.isAutomobile && this.mobile) ||
      (sticky && !this.mobile)
    )
  }

  get targetID(): string {
    return this.domConfig.targetID || this.apiConfig.targetID || this.slug
  }

  get test(): boolean {
    const params = new URLSearchParams(window.location.search)
    return params.has('test')
  }

  get title(): string {
    return this.domConfig.title || this.apiConfig.video.title
  }

  get unitType(): string {
    if (!this.mobile) {
      return AdUnit.sticky
    } else {
      return this.domConfig.unitType || AdUnit.video
    }
  }

  get volume(): number {
    // avoid a volume of '0'
    return typeof this.domConfig.volume === 'number'
      ? this.domConfig.volume / 100 || DEFAULT_VOLUME
      : DEFAULT_VOLUME
  }

  get width(): number {
    return this.domConfig.width || this.apiConfig.video.width || 0
  }

  private createPlaylist(): IPlaylistItem[] {
    if (!Array.isArray(this.apiConfig.playlist)) {
      return []
    }

    const shouldShield = !!this.domConfig.autoInsert
    const { video } = this.apiConfig
    const firstVideo = {
      duration: video.duration,
      height: video.height,
      keywords: video.keywords || '',
      meta: video.meta,
      permalink: this.domConfig.permalink || video.permalink,
      slug: video.slug,
      stream: {
        h264: this.domConfig.srcHls || video.hlsH264,
        h265: this.domConfig.srcHlsH265 || video.hlsH265,
        vp9: this.domConfig.srcDash || video.mpd
      },
      thumbnail: this.domConfig.srcPoster || video.thumbnail,
      title: video.title,
      tracks: video.tracks,
      width: video.width,
      videoNumber: video.videoNumber
    }

    const playlist = [firstVideo, ...this.apiConfig.playlist].map((item) => {
      const { vp9, h265, h264 } = item.stream
      let poster = item.thumbnail
      let src
      let type

      if (supportVP9) {
        src = vp9
        type = SourceType.hls
      } else if (supportH265) {
        src = h265
        type = SourceType.hls
      } else {
        src = h264
        type = SourceType.hls
      }

      if (shouldShield) {
        src = src.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
        poster = poster.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
      }

      return {
        duration: item.duration,
        keywords: item.keywords || '',
        permalink: item.permalink,
        poster,
        slug: item.slug,
        sources: [
          { src, type }
          // TODO: provide fallback sources once Video.js supports it for adaptive streaming
          // {
          //   src: item.stream.vp9,
          //   type: SourceType.dash
          // },
          // {
          //   src: item.stream.h265,
          //   type: SourceType.hls
          // },
          // {
          //   src: item.stream.h264,
          //   type: SourceType.hls
          // }
        ],
        textTracks: item.tracks,
        title: item.title,
        videoNumber: item.videoNumber
      }
    })

    // If this is a playlist, the order is fine. If there is a primary video that
    // needs to be played first on page load, shift it to the starting position
    if (this.targetID.indexOf(PREFIX_PLAYLIST) !== -1) {
      return playlist
    }

    const pos = this.startingPosition
    playlist.splice(pos, 0, playlist.splice(0, 1)[0])

    return playlist
  }

  private getPopoutPlacement(): PopoutPlacement {
    if (this.mobile) {
      return this.apiConfig.site.mobilePopoutPlacement
    }

    const stickyContentSide = !!this.apiConfig.site.stickyContentSide
    const contentSelector: any = this.apiConfig.site.contentSelector || null
    const sidebarSelector: any = this.apiConfig.site.sidebarSelector || null
    const contentEl = document.querySelector(contentSelector) as HTMLElement
    const sidebarEl = document.querySelector(sidebarSelector) as HTMLElement
    let contentLeft: number | boolean
    let sidebarLeft: number | boolean

    try {
      contentLeft = contentEl && contentEl.getBoundingClientRect().left
      sidebarLeft = sidebarEl && sidebarEl.getBoundingClientRect().left
    } catch (e) {
      sidebarLeft = false
      contentLeft = false
    }

    if (typeof sidebarLeft !== 'number' || typeof contentLeft !== 'number') {
      return PopoutPlacement.bottomRight
    }

    if (stickyContentSide) {
      return sidebarLeft < contentLeft
        ? PopoutPlacement.bottomRight
        : PopoutPlacement.bottomLeft
    }
    return sidebarLeft < contentLeft
      ? PopoutPlacement.bottomLeft
      : PopoutPlacement.bottomRight
  }

  private getPopoutTopMargin(): string {
    const defaultMargin = 15
    return this.mobile
      ? `${defaultMargin + this.apiConfig.site.mobilePopoutTopMargin}px`
      : '0px'
  }

  private getTargetDiv(): HTMLDivElement {
    if (this.apiConfig.targetDiv) {
      return this.apiConfig.targetDiv
    }

    const nodes = document.querySelectorAll(`div[id='${this.targetID}']`)
    const divs = Array.from(nodes) as HTMLDivElement[]
    const div = divs.find((div) => div.childElementCount === 0)

    if (!div) {
      throw new Error(`No empty target div found: ${this.targetID}`)
    }

    return div
  }
}

export interface IPlaylistItem {
  duration: number
  keywords: string
  permalink: string
  poster: string
  slug: string
  sources: ISource[]
  textTracks: ITrack[]
  title: string
  videoNumber: number
}

interface ISource {
  src: string
  type: string
}

interface ITrack {
  default: boolean
  kind: 'captions' | 'subtitles' | 'metadata' | 'descriptions' | 'chapters'
  label: string
  src: string
  srclang: string
}
